import { useQuery, useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useState } from "react";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import { grantToRoleChip } from "../lib/roleChip";
import { classifyZeroResult } from "../lib/zeroResultError";
import { navigate, routes } from "../lib/urls";
import { Button } from "./Button";
import { Modal } from "./Modal";
import { PillBadge } from "./Settings/shared";
import { Text } from "./Text";

const Content = styled("div")`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 320px;
`;

const Footer = styled("div")`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

const InlineRow = styled("span")`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

type Space = {
  id: string;
  name: string;
  defaultJoinBuiltinRoleId?: string | null;
  defaultJoinCustomRoleId?: string | null;
};

type Props = {
  open: boolean;
  onClose: () => void;
  space: Space | null;
};

export function JoinSpaceModal({ open, onClose, space }: Props) {
  const z = useZero();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [customRoles] = useQuery(queries.allRolesLite());

  const builtinId = space?.defaultJoinBuiltinRoleId ?? null;
  const customId = space?.defaultJoinCustomRoleId ?? null;
  const customRole = customId
    ? (customRoles.find((r) => r.id === customId) ?? null)
    : null;
  const chip = grantToRoleChip({
    builtinRoleId: builtinId,
    customRole,
  });

  const handleJoin = async () => {
    if (!space) return;
    setBusy(true);
    setError(null);
    const join = async () => {
      const result = z.mutate(sharedMutators.joinSpace({ spaceId: space.id }));
      const serverResult = await result.server;
      if (serverResult.type === "error") {
        const decision = classifyZeroResult(serverResult);
        setError(
          (decision.kind === "toast" && decision.message) ||
            "Failed to join space"
        );
        return;
      }
      onClose();
      navigate(routes.space, { spaceId: space.id });
    };
    await join().finally(() => setBusy(false));
  };

  if (!space) return null;

  return (
    <Modal open={open} onClose={onClose} alert>
      <Content>
        <Text variant="text-regular-md" color="var(--text-primary)">
          Join {space.name}?
        </Text>
        <InlineRow>
          <Text variant="text-regular" color="var(--text-secondary)">
            You'll join as
          </Text>
          {chip && <PillBadge icon={chip.icon}>{chip.label}</PillBadge>}
        </InlineRow>
        {error && (
          <Text variant="text-small" color="var(--text-danger)">
            {error}
          </Text>
        )}
        <Footer>
          <Button size="l" variant="subtle" onClick={onClose}>
            Cancel
          </Button>
          <Button size="l" onClick={handleJoin} disabled={busy}>
            {busy ? "Joining…" : "Join"}
          </Button>
        </Footer>
      </Content>
    </Modal>
  );
}
